import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
	Box,
	Button,
	Chip,
	Stack,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import {
	type ChangeEvent,
	type DragEvent,
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { createComment } from "../../../api/comments/comments";
import { uploadAttachment } from "../../../api/person/person-attachment";
import type { ActivityPanelHandle, ActivityPanelProps } from "./panel-types";

interface NotePanelProps extends ActivityPanelProps {
	personId: number;
}

export const NotePanel = forwardRef<ActivityPanelHandle, NotePanelProps>(
	({ personId, onCanSaveChange }, ref) => {
		const theme = useTheme();
		const [content, setContent] = useState("");
		const [isDragging, setIsDragging] = useState(false);
		const [pendingFiles, setPendingFiles] = useState<File[]>([]);
		const fileInputRef = useRef<HTMLInputElement>(null);

		const canSave = content.trim().length > 0 || pendingFiles.length > 0;

		useEffect(() => {
			onCanSaveChange(canSave);
		}, [canSave, onCanSaveChange]);

		useImperativeHandle(ref, () => ({
			save: async () => {
				for (const file of pendingFiles) {
					await uploadAttachment(file, personId);
				}
				if (content.trim()) {
					await createComment("persons", personId, content);
				}
			},
		}));

		const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			setIsDragging(true);
		};

		const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			setIsDragging(false);
		};

		const handleDrop = (e: DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			setIsDragging(false);
			const files = Array.from(e.dataTransfer.files);
			if (files.length > 0) {
				setPendingFiles((prev) => [...prev, ...files]);
			}
		};

		const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files ? Array.from(e.target.files) : [];
			if (files.length > 0) {
				setPendingFiles((prev) => [...prev, ...files]);
			}
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		};

		const handleRemovePendingFile = (index: number) => {
			setPendingFiles((prev) => prev.filter((_, i) => i !== index));
		};

		return (
			<Stack spacing={2}>
				<Box
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					sx={{
						border: isDragging
							? `2px dashed ${theme.palette.primary.main}`
							: `1px dashed ${theme.palette.divider}`,
						borderRadius: 1,
						transition: "border 0.2s",
					}}
					data-component="drop-zone"
				>
					<TextField
						label="Note"
						value={content}
						onChange={(e) => setContent(e.target.value)}
						multiline
						minRows={8}
						fullWidth
						placeholder="Write your note... (or drag & drop files)"
						data-component="content-input"
						sx={{
							"& .MuiOutlinedInput-root": {
								backgroundColor: isDragging
									? theme.palette.action.hover
									: "transparent",
							},
						}}
					/>
				</Box>

				<Stack direction="row" spacing={1} alignItems="center">
					<Button
						variant="outlined"
						size="small"
						startIcon={<AttachFileIcon />}
						onClick={() => fileInputRef.current?.click()}
						data-component="attach-files-button"
					>
						Attach Files
					</Button>
					<Typography variant="caption" color="text.secondary">
						or drag and drop files above
					</Typography>
				</Stack>

				{pendingFiles.length > 0 && (
					<Stack
						direction="row"
						spacing={1}
						flexWrap="wrap"
						useFlexGap
						data-component="pending-files"
					>
						{pendingFiles.map((file, idx) => (
							<Chip
								key={idx}
								label={file.name}
								size="small"
								onDelete={() => handleRemovePendingFile(idx)}
								icon={<AttachFileIcon />}
								data-component="pending-file-chip"
							/>
						))}
					</Stack>
				)}

				<input
					ref={fileInputRef}
					type="file"
					onChange={handleFileSelect}
					accept="image/*,.pdf,.docx,.doc"
					multiple
					style={{ display: "none" }}
				/>
			</Stack>
		);
	}
);

NotePanel.displayName = "NotePanel";
